<script setup>
import { computed, onActivated, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useCampaignDrip } from 'dashboard/composables/useCampaignDrip';
import { useAdmin } from 'dashboard/composables/useAdmin';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import EditScheduledCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/EditScheduledCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import CampaignProgressDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignProgressDialog.vue';
import WhatsAppCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/WhatsAppCampaignEmptyState.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();
const { handleStart, handlePause, handleResume, handleCancel, handlePreview } =
  useCampaignDrip();

const selectedCampaign = ref(null);
// Modal central (DS Dialog) aberto via ref — disparado pelo botão do topo OU pelo
// CTA "criar nova" embaixo da lista.
const campaignDialogRef = ref(null);
const openCreate = () => campaignDialogRef.value?.open();

// Decisão 30: o canal escolhido no cartão "Nova ação de marketing" já entra
// pronto. A rota é quem diz qual é (campaigns_whatsapp = QR code ou oficial), e
// o canal viaja como família de canais na abertura do diálogo, não pela URL: a
// URL só carrega o ?new=1 que já existia. Por "Nova campanha" dentro de
// Campanhas não vai família nenhuma, e o passo 1 aparece normalmente.
const WHATSAPP_CHANNEL_FAMILY = ['waha', 'official'];
const openCreateFromHub = () =>
  campaignDialogRef.value?.open({ channelFamily: WHATSAPP_CHANNEL_FAMILY });

// SiteUp (Hub): "Criar" na tela de início de Ações de Marketing chega com
// ?new=1 e já abre o formulário. A query é consumida na chegada (a página
// fica em keep-alive, por isso também em onActivated), para o F5 não reabrir.
const route = useRoute();
const router = useRouter();
const openFromQuery = () => {
  // Sem router (specs montam a página sozinha), não há query a consumir.
  if (String(route?.query?.new || '') !== '1') return;
  const cleaned = { ...route.query };
  delete cleaned.new;
  router?.replace({ query: cleaned });
  // Criar é só de admin (o botão do topo já esconde): o link não abre o formulário.
  if (!isAdmin.value) return;
  openCreateFromHub();
};
onMounted(openFromQuery);
onActivated(openFromQuery);
watch(() => route?.query?.new, openFromQuery);
const editScheduledCampaignDialogRef = ref(null);
const handleEdit = campaign =>
  editScheduledCampaignDialogRef.value?.open(campaign);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);
const hasCampaignLoadError = computed(() => uiFlags.value.fetchError);

const confirmDeleteCampaignDialogRef = ref(null);
const progressDialogRef = ref(null);
const handleProgress = campaign => progressDialogRef.value?.open(campaign);

const WhatsAppCampaigns = computed(
  () => getters['campaigns/getWhatsAppCampaigns'].value
);

const hasNoWhatsAppCampaigns = computed(
  () => WhatsAppCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.WHATSAPP.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
    @click="openCreate"
  >
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="hasCampaignLoadError"
      class="flex flex-col items-center justify-center gap-3 py-10 text-n-slate-11"
      role="alert"
    >
      <p>{{ t('CAMPAIGN.LOAD_ERROR') }}</p>
      <Button
        data-testid="campaign-retry"
        :label="t('CAMPAIGN.RETRY')"
        variant="faded"
        color="slate"
        size="sm"
        @click="store.dispatch('campaigns/get')"
      />
    </div>
    <template v-else-if="!hasNoWhatsAppCampaigns">
      <CampaignList
        :campaigns="WhatsAppCampaigns"
        @delete="handleDelete"
        @edit="handleEdit"
        @start="handleStart"
        @pause="handlePause"
        @resume="handleResume"
        @cancel="handleCancel"
        @preview="handlePreview"
        @progress="handleProgress"
      />
      <!-- Criar nova campanha logo abaixo das campanhas recentes -->
      <div class="flex justify-center mt-4">
        <Button
          v-if="isAdmin"
          :label="t('CAMPAIGN.WHATSAPP.NEW_CAMPAIGN')"
          icon="i-ri-add-line"
          size="sm"
          @click="openCreate"
        />
      </div>
    </template>
    <WhatsAppCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.WHATSAPP.EMPTY_STATE.TITLE')"
      :subtitle="
        isAdmin
          ? t('CAMPAIGN.WHATSAPP.EMPTY_STATE.SUBTITLE')
          : t('CAMPAIGN.VIEW_ONLY_EMPTY_HINT')
      "
    />
    <WhatsAppCampaignDialog ref="campaignDialogRef" />
    <EditScheduledCampaignDialog ref="editScheduledCampaignDialogRef" />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <CampaignProgressDialog ref="progressDialogRef" />
  </CampaignLayout>
</template>
